<script setup lang="ts">
import { isEmail } from '@@/bits/email'
import { __ } from '@@/bits/intl'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

type InputFieldRules = { required?: boolean; minLength?: number; email?: boolean }

const props = withDefaults(
  defineProps<{
    /**
     * Determines label name
     */
    name: string
    /**
     * Insert id to bind label/input, which will enable autofocus
     * on input field when label is clicked. This value will also be used to locate the
     * correct input element that is being updated.
     */
    id: string
    /**
     * Bind input value attribute with value prop
     */
    value: string
    placeholder?: string
    /**
     * Insert rules for input.
     * Supported rules are: required, minLength, email
     */
    rules?: InputFieldRules
    type?: 'text' | 'email' | 'url'
  }>(),
  {
    placeholder: '',
    rules: () => ({ required: false }),
    type: 'text',
  },
)

const emit = defineEmits<{
  (e: 'update', payload: { id: string; name: string; value: string }): void
}>()

const { isDarkMode } = storeToRefs(useDarkModeStore())

const inputRef = ref<HTMLInputElement>()

const requiredLabel = computed<string>(() => {
  return props.rules.required ? `${props.name} *` : props.name
})

const isRequired = computed<boolean>(() => {
  return !!props.rules.required
})

const wrapperClass = computed<Record<string, boolean>>(() => {
  return {
    'dark-mode': isDarkMode.value,
    'input-wrapper': true,
  }
})

function input(ev): void {
  validate(ev.target.value)
  /**
   * Triggers when the input value changes
   *
   * @property {string} id Data required to specify which input value it's updating
   * @property {string} name Name that was set before the change
   * @property {string} value Data that updates new input value (Updates parent state with value)
   */
  emit('update', {
    id: props.id,
    name: props.name,
    value: ev.target.value,
  })
}

function validateEmail(email: string, element: HTMLInputElement): void {
  const validEmail = isEmail(email)

  // Set email validation error message
  if (!validEmail && element) {
    element.setCustomValidity(__('Please enter valid email'))
  } else {
    element.setCustomValidity('')
  }
}

function validate(value): void {
  // Show error messages based on rules prop
  const inputElement = inputRef.value as HTMLInputElement

  if (inputElement) {
    if (props.rules.minLength && value.length < props.rules.minLength) {
      const errorMessage = __('Minimum length is %{minimumLength}', { minimumLength: props.rules.minLength })
      inputElement.setCustomValidity(errorMessage)
    } else if (props.rules.email) {
      validateEmail(value, inputElement)
    } else {
      inputElement.setCustomValidity('')
    }
  }
}
</script>

<template>
  <div :class="wrapperClass">
    <div class="label">
      <label :for="id">{{ requiredLabel }}</label>
    </div>
    <input
      :id="id"
      ref="inputRef"
      class="input-field"
      :value="value"
      :type="type"
      :required="isRequired"
      :placeholder="placeholder"
      @input="input"
    />
  </div>
</template>

<style lang="scss">
@import '../library/v4/stylesheets/colors';

:root {
  // Colors for input field
  --input-text-color: #{$dark-grey-1};
  --input-field-bg-color: #{$white};
  --input-field-bd-color: #{$dark-grey-7};
  --input-field-ph-text-color: #909090;
  --input-field-hl-color: #{$yellow-color};
}

.dark-mode {
  --input-text-color: #{$light-grey-text-1};
  --input-field-bg-color: #{$dark-grey-4};
  --input-field-bd-color: #{$white};
  --input-field-ph-text-color: #{$light-grey-text-2};
}
</style>
<style lang="scss" scoped>
.input-wrapper {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 14px;
  word-break: break-word;
  color: var(--input-text-color);
  margin: 0px 16px 10px;
}

.input-field {
  font-size: 16px;
  line-height: 18px;
  color: var(--input-text-color);
  background: var(--input-field-bg-color);
  transition: all 0.3s;

  width: 100%;
  height: 46px;
  padding: 0px 16px;
  border-radius: 16px;
  box-sizing: border-box;
  border: 2px solid var(--input-field-bd-color);
  box-shadow: none;
  outline: none;

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0px 1000px var(--input-field-bg-color) inset;
    -webkit-text-fill-color: var(--input-text-color);
  }

  &:focus {
    border: 2px solid var(--input-field-hl-color);
  }

  &::placeholder {
    color: var(--input-field-ph-text-color);
  }
}
</style>
